import { useLocation, useNavigate } from 'react-router-dom';
import { AlertTriangle, Navigation2, Signpost, ArrowLeft } from 'lucide-react';
import { TrafficIncidentMap } from '../components/TrafficIncidentMap';
import type { TrafficIncident } from '../types';

const SEVERITY_STYLE: Record<string, string> = {
  high: 'bg-red-100 text-red-700',
  medium: 'bg-amber-100 text-amber-700',
  low: 'bg-green-100 text-green-700',
  unknown: 'bg-gray-100 text-gray-500',
};

// Detail view for a single traffic incident headline clicked from the dashboard's Traffic News
// section — the incident (an ephemeral row from a third-party feed with no per-id backend
// lookup) is passed via router state from TrafficNewsSection's <Link state={incident}>, mirroring
// the mobile app's traffic-news/[id] route (which passes the same fields via router params).
export default function TrafficNewsDetailPage() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const incident = state as TrafficIncident | null;

  if (!incident) {
    return (
      <div className="page-shell-tight text-center">
        <p className="text-sm text-gray-500 mb-3">This incident is no longer available.</p>
        <button onClick={() => navigate('/my-dashboard')} className="text-sm text-blue-600 hover:underline">
          ← Back to Dashboard
        </button>
      </div>
    );
  }

  const sev = SEVERITY_STYLE[incident.severity] ?? SEVERITY_STYLE.unknown;

  return (
    <div data-testid="traffic-news-detail-page" className="page-shell-tight space-y-4">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <TrafficIncidentMap lat={incident.lat} lng={incident.lng} title={incident.title} />

      <div className="flex items-center gap-2">
        <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full capitalize ${sev}`}>{incident.severity}</span>
        <span className="text-xs text-gray-400">{incident.category}</span>
      </div>

      <h1 data-testid="traffic-news-detail-title" className="text-lg font-bold text-gray-900">{incident.title}</h1>

      {incident.roadName && (
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Signpost className="w-4 h-4 text-gray-400" /> {incident.roadName}
        </div>
      )}

      <div className="flex items-center gap-2 text-sm text-gray-600">
        <Navigation2 className="w-4 h-4 text-gray-400" /> {incident.distanceMiles.toFixed(1)} miles away
      </div>

      <p className="text-sm text-gray-700 leading-relaxed flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
        <span>{incident.description}</span>
      </p>

      {incident.startDate && (
        <p className="text-xs text-gray-400">
          Reported {new Date(incident.startDate).toLocaleString('en-GB')}
        </p>
      )}
    </div>
  );
}
